// FE-side parallel of server/src/lib/evidenceFileTypes.ts. keep the
// allow-list + preview-mime sets in sync between client + server so
// the FE pre-check rejects what the server would reject and the
// preview modal only opens for types the server serves inline

export const EVIDENCE_ALLOWED_EXTENSIONS = [
  'pdf', 'docx', 'xlsx', 'doc', 'xls', 'png', 'jpg', 'jpeg',
] as const

export const EVIDENCE_ALLOWED_LABEL =
  'PDF, DOCX, XLSX, DOC, XLS, PNG, JPG'

// file size above which the FE shows an upfront warning. CF Tunnel's
// observed ~5-11s killer for multipart bodies ≥10MB (parse-assessment
// + evidence-upload diagnostics, commits 4714c6d + ce6c908) means
// uploads over this threshold may fail until the chunked-upload
// rebuild ships. 8 MB is the warning point, well below the failure
// threshold so the user has runway to compress / split
export const EVIDENCE_SOFT_SIZE_WARNING_BYTES = 8 * 1024 * 1024
export const EVIDENCE_SOFT_SIZE_WARNING_LABEL = '8 MB'

export function getEvidenceExtension(filename: string): string {
  const dot = filename.lastIndexOf('.')
  if (dot < 0) return ''
  return filename.slice(dot + 1).toLowerCase()
}

export function isEvidenceTypeAllowed(filename: string): boolean {
  const ext = getEvidenceExtension(filename)
  return (EVIDENCE_ALLOWED_EXTENSIONS as readonly string[]).includes(ext)
}

// three previewable types, match server/src/lib/evidenceFileTypes.ts's
// EVIDENCE_INLINE_MIME_TYPES set. mime-type can be null on legacy rows
// (column is nullable in the schema). the helper also checks the file
// extension as a fallback so older profile-bound rows that pre-date
// Phase 3.6 still get a preview button when their filename ends in
// .pdf / .png / .jpg
export function canPreviewInline(file: { mime_type: string | null; original_filename: string }): boolean {
  if (file.mime_type === 'application/pdf') return true
  if (file.mime_type === 'image/png')       return true
  if (file.mime_type === 'image/jpeg')      return true
  const ext = getEvidenceExtension(file.original_filename)
  return ext === 'pdf' || ext === 'png' || ext === 'jpg' || ext === 'jpeg'
}

export function formatFileSize(bytes: number): string {
  if (bytes < 1024)              return `${bytes} B`
  if (bytes < 1024 * 1024)       return `${(bytes / 1024).toFixed(0)} KB`
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`
}
